import {QuotesQuoteBillingTypeChoices} from 'generated/graphql'

/**
 * Quote billing rules use the union of all `user.organizations` (GraphQL), matching
 * backend `get_user_organizations()` and `billing_type_allowed_for_user_organizations`
 * in `apps/quotes.forms.QuoteForm.clean` — not the single `user.organization`.
 */

export type BillingTypeOption = {
    value: QuotesQuoteBillingTypeChoices
    label: string
}

export type OrganizationWithBrazilFlag = {
    isBrazil?: boolean | null
}

/** Tipos seleccionables al criar cotação (org Brasil). */
export const BRAZIL_ORGANIZATION_SELECTABLE_BILLING_TYPES: BillingTypeOption[] = [
    {value: QuotesQuoteBillingTypeChoices.Direct, label: '6A - Venda direta ao agricultor'},
    {value: QuotesQuoteBillingTypeChoices.Urgency, label: '6B - Urgência máquina parada'},
    {value: QuotesQuoteBillingTypeChoices.Indirect, label: '6C - Venda indireta (compra própria dealer)'},
]

/** Alinha com `BILLING_TYPES_FOR_BRAZIL_ORGANIZATION` no backend (inclui tipos atribuídos pelo fluxo). */
export const BRAZIL_ORGANIZATION_BILLING_TYPES: BillingTypeOption[] = [
    ...BRAZIL_ORGANIZATION_SELECTABLE_BILLING_TYPES,
    {value: QuotesQuoteBillingTypeChoices.TryAndBuy, label: 'N8 - Demonstração'},
    {value: QuotesQuoteBillingTypeChoices.TryAndBuyBilling, label: '6E - Try and Buy - Faturamento simbólico'},
]

/** Alinha com `BILLING_TYPES_FOR_INTERNATIONAL_ORGANIZATION` no backend. */
export const INTERNATIONAL_ORGANIZATION_BILLING_TYPES: BillingTypeOption[] = [
    {value: QuotesQuoteBillingTypeChoices.DealerOsa, label: '6L - Venda Pedido Dealer OSA'},
    {value: QuotesQuoteBillingTypeChoices.MachineStoppedOsa, label: '6M – Venda Pedido Máq. Parada OSA'},
    {value: QuotesQuoteBillingTypeChoices.ScheduledOsa, label: '6N – Venda Pedido Programado OSA'},
]

function uniqueBillingTypeOptions(optionSets: BillingTypeOption[][]): BillingTypeOption[] {
    const seen = new Set<QuotesQuoteBillingTypeChoices>()
    const merged: BillingTypeOption[] = []
    for (const options of optionSets) {
        for (const option of options) {
            if (seen.has(option.value)) continue
            seen.add(option.value)
            merged.push(option)
        }
    }
    return merged
}

function billingTypeOptionSetsForOrganizations(
    organizations: OrganizationWithBrazilFlag[],
    allowedTypes: {brazil: BillingTypeOption[]; international: BillingTypeOption[]},
): BillingTypeOption[][] {
    const optionSets: BillingTypeOption[][] = []
    if (organizations.some(org => org.isBrazil === true)) {
        optionSets.push(allowedTypes.brazil)
    }
    if (organizations.some(org => org.isBrazil === false)) {
        optionSets.push(allowedTypes.international)
    }
    return optionSets
}

/** True when any organization is non-Brazil (`QuoteForm` country validation for "others"). */
export function userOrganizationsCanQuoteOtherCountries(
    organizations?: OrganizationWithBrazilFlag[] | null,
): boolean {
    return organizations?.some(org => org.isBrazil === false) ?? false
}

/** Selectable billing types = union of options for each org type the user belongs to. */
export function getBillingTypeOptionsForUserOrganizations(
    organizations?: OrganizationWithBrazilFlag[] | null,
): BillingTypeOption[] {
    const orgs = organizations ?? []
    if (!orgs.length) {
        return BRAZIL_ORGANIZATION_SELECTABLE_BILLING_TYPES
    }

    return uniqueBillingTypeOptions(
        billingTypeOptionSetsForOrganizations(orgs, {
            brazil: BRAZIL_ORGANIZATION_SELECTABLE_BILLING_TYPES,
            international: INTERNATIONAL_ORGANIZATION_BILLING_TYPES,
        }),
    )
}

export function getAllowedBillingTypesForUserOrganizations(
    organizations?: OrganizationWithBrazilFlag[] | null,
): BillingTypeOption[] {
    const orgs = organizations ?? []
    if (!orgs.length) {
        return BRAZIL_ORGANIZATION_BILLING_TYPES
    }

    return uniqueBillingTypeOptions(
        billingTypeOptionSetsForOrganizations(orgs, {
            brazil: BRAZIL_ORGANIZATION_BILLING_TYPES,
            international: INTERNATIONAL_ORGANIZATION_BILLING_TYPES,
        }),
    )
}

/** Allowed when any user organization permits the type. */
export function isBillingTypeAllowedForUserOrganizations(
    billingType: string | undefined | null,
    organizations?: OrganizationWithBrazilFlag[] | null,
): boolean {
    if (!billingType) return false

    const normalizedBillingType = billingType.toUpperCase()
    return getAllowedBillingTypesForUserOrganizations(organizations).some(
        option => option.value === normalizedBillingType,
    )
}
